import { memo, useEffect, useState } from 'react'
import Marquee from 'react-fast-marquee'
import { motion } from 'framer-motion'

const brands = [
  { name: 'Acer', src: '/brands/acer.webp' },
  { name: 'AOC', src: '/brands/aoc.webp' },
  { name: 'Dell', src: '/brands/dell.webp' },
  { name: 'Gateway', src: '/brands/gateway.webp' },
  { name: 'MSI', src: '/brands/msi.webp' },
  { name: 'Samsung', src: '/brands/samsung.webp' },
  { name: 'LG', src: '/brands/lg.webp' },
  { name: 'Sony', src: '/brands/sony.webp' },
  { name: 'TCL', src: '/brands/tcl.webp' },
  { name: 'HP', src: '/brands/hp.webp' },
  { name: 'Panasonic', src: '/brands/panasonic.webp' },
  { name: 'Toshiba', src: '/brands/toshiba.webp' },
  { name: 'Compaq', src: '/brands/compaq.webp' },
  { name: 'QBEX', src: '/brands/qbex.webp' },
  { name: 'Itautec', src: '/brands/itautec.webp' },
  { name: 'Megaware', src: '/brands/megaware.webp' },
]

function BrandLogo({ brand }) {
  return (
    <div
      className="mx-3 xs:mx-4 sm:mx-5 flex-shrink-0 group/logo"
      aria-hidden="false"
    >
      <div
        className="flex h-24 w-36 xs:h-28 xs:w-40 sm:h-32 sm:w-44 items-center justify-center 
                   rounded-xl border border-gray-100 bg-white/60 p-4 xs:p-5 
                   shadow-soft transition-all duration-200 
                   group-hover/logo:-translate-y-0.5 group-hover/logo:border-primary-200 
                   group-hover/logo:bg-white group-hover/logo:shadow-medium"
      >
        <img
          src={brand.src}
          alt={`Logo ${brand.name}`}
          loading="lazy"
          decoding="async"
          fetchPriority="low"
          className="max-h-12 max-w-[80%] object-contain grayscale brightness-90 
                     transition-all duration-300 
                     group-hover/logo:grayscale-0 group-hover/logo:brightness-100"
        />
      </div>
    </div>
  )
}

const BrandsCarousel = () => {
  const [shouldAnimate, setShouldAnimate] = useState(true)

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)')
    const updateMotionPreference = () => setShouldAnimate(!mediaQuery.matches)

    updateMotionPreference()
    mediaQuery.addEventListener('change', updateMotionPreference)
    return () => mediaQuery.removeEventListener('change', updateMotionPreference)
  }, [])

  return (
    <section
      className="py-6 xs:py-8 xs2:py-10 sm:py-12 md:py-16 bg-gradient-to-b 
                 from-white to-gray-50 dark:from-gray-900 dark:to-gray-950 overflow-hidden"
      aria-label="Marcas atendidas"
    >
      <div className="container-responsive">
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-xl xs:text-2xl xs2:text-2xl xs3:text-3xl sm:text-3xl 
                    md:text-4xl font-bold text-center text-gray-800 dark:text-gray-100 
                    mb-4 xs:mb-5 xs2:mb-6 sm:mb-8"
        >
          Marcas que atendemos
          <div
            className="w-16 xs:w-20 sm:w-24 md:w-32 h-0.5 xs:h-1 bg-primary-600 
                        mx-auto mt-3 xs:mt-4 sm:mt-5 rounded-full"
          />
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
          viewport={{ once: true }}
          className="text-sm xs:text-base text-gray-600 dark:text-gray-400 text-center max-w-2xl mx-auto 
                    mb-2 xs:mb-3 px-4"
        >
          Trabalhamos com as principais marcas do mercado — peças de qualidade e técnicos especializados.
        </motion.p>
        <p className="text-xs text-gray-400 text-center mb-6 xs:mb-8">
          Passe o mouse para pausar
        </p>

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 0.6, delay: 0.2 }}
          viewport={{ once: true }}
          className="brands-marquee relative"
        >
          {shouldAnimate ? (
            <Marquee
              pauseOnHover
              speed={35}
              gradient
              gradientColor="#f9fafb"
              gradientWidth={80}
              autoFill
            >
              {brands.map((brand) => (
                <BrandLogo key={brand.name} brand={brand} />
              ))}
            </Marquee>
          ) : (
            <div className="flex flex-wrap items-center justify-center gap-4 px-4">
              {brands.map((brand) => (
                <BrandLogo key={brand.name} brand={brand} />
              ))}
            </div>
          )}
        </motion.div>
      </div>
    </section>
  )
}

export default memo(BrandsCarousel)
